/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import memberRolesTableData from "layouts/tables/data/memberRolesTableData";
import fetchWithAuth from "layouts/tables/data/api"; // Import the custom fetch wrapper

// API Configuration
import appSettings from "appsettings.json";

import { useState, useEffect } from "react";

function MemberRoles() {
  const [memberRoles, setMemberRoles] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedMemberRole, setSelectedMemberRole] = useState(null);

  const buildGetAllMemberRolesUrl = () => {
    const base = `${appSettings.baseUrl}${appSettings.endpoints.getAllMemberRoles}`;
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}_ts=${Date.now()}`;
  };

  const fetchMemberRoles = async () => {
    setLoading(true);
    setError(null);
    const url = buildGetAllMemberRolesUrl();
    try {
      const response = await fetchWithAuth(url, {
        method: "GET",
        cache: "no-store",
      });
      if (!response.ok) throw new Error("An error occurred while fetching member roles.");
      const data = await response.json();
      if (data && data.$values) {
        setMemberRoles(data.$values);
      } else if (Array.isArray(data)) {
        setMemberRoles(data);
      } else {
        setMemberRoles([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemberRoles();
  }, []);

  const handleOpenDeleteConfirm = (memberRole) => {
    setSelectedMemberRole(memberRole);
    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    setOpenDeleteConfirm(false);
    setSelectedMemberRole(null);
  };

  const handleDeleteDialogClose = (_, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    handleCloseDeleteConfirm();
  };

  const handleConfirmDelete = async () => {
    const selectedId =
      selectedMemberRole?.memberRoleId ??
      selectedMemberRole?.MemberRoleId ??
      selectedMemberRole?.id ??
      selectedMemberRole?.Id ??
      null;

    if (selectedId === null) {
      setError("Unable to delete member role: invalid member role id.");
      return;
    }

    setDeleting(true);
    setError(null);

    const formBody = new URLSearchParams();
    formBody.append("id", String(selectedId));
    const url = `${appSettings.baseUrl}${appSettings.endpoints.deleteMemberRole}`;

    try {
      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        let errorMessage = "An error occurred while deleting member role.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // Keep default error message when response body is not JSON.
        }

        throw new Error(errorMessage);
      }

      handleCloseDeleteConfirm();
      await fetchMemberRoles();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const { columns, rows } = memberRolesTableData(memberRoles, handleOpenDeleteConfirm);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDTypography variant="h6" color="white">
                  Member Roles
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <CircularProgress color="success" />
                  </MDBox>
                ) : error ? (
                  <MDBox p={3}>
                    <MDTypography variant="button" color="error">
                      Error: {error}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    canSearch
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 5, entries: [5, 10, 15, 20] }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog open={openDeleteConfirm} onClose={handleDeleteDialogClose} fullWidth maxWidth="xs">
        <DialogTitle>Delete Member Role</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to delete this member role?
          </MDTypography>
          {selectedMemberRole && (
            <MDBox mt={1}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                {selectedMemberRole.memberName} - {selectedMemberRole.roleName}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleCloseDeleteConfirm} color="secondary" disabled={deleting}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmDelete}
            variant="gradient"
            color="error"
            disabled={deleting}
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default MemberRoles;
